import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import debug from 'debug';
import { Fragment, useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { isArabic } from '@/config/constants';
import { themeSelector } from '@/state/config';
import styles from './Card.module.scss';
const log = debug('moveo:card');
export const CarouselCard = ({ card, onPostback, className, disabled, onWebviewClick, onButtonClick, }) => {
    const theme = useRecoilValue(themeSelector);
    const { media, buttons, title, subtitle, default_action: defaultAction, } = card;
    const hasDefaultAction = defaultAction && Object.values(defaultAction).some((value) => value !== '');
    const resolvedMedia = useMemo(() => {
        switch (media.type) {
            case 'image':
                return _jsx("img", { src: media.url, alt: title });
            case 'video':
                // eslint-disable-next-line jsx-a11y/media-has-caption
                return _jsx("video", { src: media.url, controls: true });
            default:
                return null;
        }
    }, [media.type, media.url, title]);
    const content = useMemo(() => (_jsxs("div", { children: [_jsx("div", { className: styles.mediaContainer, children: resolvedMedia }), _jsxs("div", { className: styles.headingContainer, children: [_jsx("h4", { className: styles.title, children: title }), _jsx("p", { className: styles.subtitle, children: subtitle })] })] })), [resolvedMedia, subtitle, title]);
    const getButtonProps = useCallback((button, isDefaultAction) => {
        switch (button.type) {
            case 'postback':
                return (_jsx("button", { dir: isArabic(button.label), className: !isDefaultAction ? styles.button : styles.defaultActionButton, disabled: disabled, onClick: () => {
                        onButtonClick({
                            type: 'carousel_button_postback',
                            label: button.label,
                            value: button.value,
                        });
                        if (onPostback) {
                            onPostback(button.value, button.label);
                        }
                    }, children: isDefaultAction ? content : button.label }));
            case 'url':
                return (_jsx("a", { className: !isDefaultAction ? styles.button : '', dir: isArabic(button.label), href: button.url, target: "_blank", rel: "noreferrer", onClick: () => onButtonClick({
                        type: 'carousel_button_url',
                        label: button.label,
                        value: button.url,
                    }), children: isDefaultAction ? content : button.label }));
            case 'phone':
                return (_jsx("a", { dir: isArabic(button.label), className: !isDefaultAction ? styles.button : '', href: `tel:${button.value}`, target: "_blank", rel: "noreferrer", onClick: () => onButtonClick({
                        type: 'carousel_button_phone',
                        label: button.label,
                        value: button.value,
                    }), children: isDefaultAction ? content : button.label }));
            case 'webview':
                return (_jsx("button", { dir: isArabic(button.label), className: !isDefaultAction ? styles.button : '', disabled: disabled, onClick: () => {
                        onButtonClick({
                            type: 'carousel_button_webview',
                            label: button.label,
                            value: button.url,
                        });
                        if (onWebviewClick) {
                            onWebviewClick(button);
                        }
                    }, children: isDefaultAction ? content : button.label }));
            default:
                log(`Unsupported button type: ${button}`, button);
                return null;
        }
    }, [content, disabled, onButtonClick, onPostback, onWebviewClick]);
    return (_jsxs("div", { className: cn(styles.card, className, styles[theme]), children: [hasDefaultAction ? _jsx(_Fragment, { children: getButtonProps(defaultAction, true) }) : content, _jsx("div", { className: styles.buttonsContainer, children: buttons.map((button, i) => (_jsx(Fragment, { children: getButtonProps(button) }, `${i}_${button.type}`))) })] }));
};
